import { createPortal } from "react-dom";
import { useRef, useState } from "react";
import { X, Loader2, FileUp, FileText, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import MultiPick from "@/components/agreements/MultiPick";

const EMPTY = { title: "", type: "waiver", content: "", pdf_path: "", pdf_filename: "", required_for: [], location_ids: [],
  service_types: [], service_ids: [], package_ids: [], membership_keys: [], active: true, is_template: false };

// Create / edit an agreement. `targets` lists which assignment pickers to show.
export default function AgreementEditorModal({ agreement, base, options = {}, targets = [], template = false, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...(agreement || {}), is_template: template || !!agreement?.is_template });
  const [busy, setBusy] = useState("");
  const pdfRef = useRef(null);
  const importRef = useRef(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const editing = !!agreement?.id;

  const upload = async (file, kind) => {
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(kind === "pdf" ? "/agreements-hub/upload-pdf" : "/agreements-hub/import-text", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (kind === "pdf") setF((x) => ({ ...x, pdf_path: data.pdf_path, pdf_filename: data.pdf_filename }));
      else { setF((x) => ({ ...x, content: data.content })); toast.success("Text imported — review and edit below."); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = editing ? await api.put(`${base}/${agreement.id}`, f) : await api.post(base, f);
      toast.success(editing && data.version > agreement.version ? `Saved as version ${data.version}` : "Saved");
      onSaved && onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(""); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[210] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" data-testid="agreement-editor">
      <div className="bg-[#FDFBF7] w-full sm:max-w-3xl max-h-[94vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 sm:p-7">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="font-serif text-2xl text-[#1A242B]">{editing ? "Edit agreement" : template ? "New template" : "New agreement"}</h3>
            {editing && <p className="text-xs text-[#8FA89B] mt-1">Currently version {agreement.version}. Changing the title, text or PDF saves a new version — past signatures stay linked to the version they signed.</p>}
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="agreement-editor-close" className="h-10 w-10 grid place-items-center rounded-full hover:bg-black/5"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-[1fr_200px] gap-3">
          <input value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="Agreement title" data-testid="agreement-editor-title" className="eyw-input w-full" />
          <select value={f.type} onChange={(e) => set("type")(e.target.value)} data-testid="agreement-editor-type" className="eyw-input w-full">
            {(options.types || ["waiver", "liability", "participation"]).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button type="button" onClick={() => importRef.current?.click()} data-testid="agreement-import-text" className="eyw-btn-ghost !py-2 !px-3 text-xs">
            {busy === "import" ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />} Import text (.docx, .pdf, .txt)
          </button>
          <button type="button" onClick={() => pdfRef.current?.click()} data-testid="agreement-attach-pdf" className="eyw-btn-ghost !py-2 !px-3 text-xs">
            {busy === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} {f.pdf_path ? "Replace PDF" : "Attach PDF document"}
          </button>
          <input ref={importRef} type="file" accept=".docx,.pdf,.txt,.md" className="hidden" onChange={(e) => { upload(e.target.files[0], "import"); e.target.value = ""; }} />
          <input ref={pdfRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { upload(e.target.files[0], "pdf"); e.target.value = ""; }} />
        </div>
        {f.pdf_path && (
          <div className="mt-2 flex items-center gap-2 text-sm rounded-xl bg-[#144F75]/[0.05] px-3 py-2" data-testid="agreement-pdf-attached">
            <FileText size={15} className="text-[#144F75]" />
            <a href={`/api/files/${f.pdf_path}`} target="_blank" rel="noreferrer" className="text-[#144F75] underline truncate flex-1">{f.pdf_filename || "Agreement PDF"}</a>
            <button type="button" onClick={() => setF((x) => ({ ...x, pdf_path: "", pdf_filename: "" }))} aria-label="Remove PDF" data-testid="agreement-pdf-remove" className="h-9 w-9 grid place-items-center rounded-full hover:bg-black/5"><Trash2 size={14} /></button>
          </div>
        )}
        <div className="mt-3">
          <RichTextEditor value={f.content} onChange={set("content")} testid="agreement-editor-content" placeholder="Agreement text (optional if a PDF is attached)" minHeight={200} enableBlocks={false} media={false} />
        </div>
        {targets.length > 0 && !f.is_template && (
          <div className="mt-5">
            <p className="eyw-label mb-2">Required for</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {targets.includes("membership_all") && (
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input type="checkbox" checked={f.required_for.includes("membership")} data-testid="agreement-required-membership"
                    onChange={(e) => set("required_for")(e.target.checked ? [...new Set([...f.required_for, "membership"])] : f.required_for.filter((r) => r !== "membership"))} className="accent-[#144F75]" />
                  Every member before any booking (platform-wide)
                </label>
              )}
              {targets.includes("services") && <MultiPick label="Services" options={options.services} value={f.service_ids} onChange={set("service_ids")} testid="agreement-pick-services" />}
              {targets.includes("packages") && <MultiPick label="Offerings & packages" options={options.packages} value={f.package_ids} onChange={set("package_ids")} testid="agreement-pick-packages" />}
              {targets.includes("memberships") && <MultiPick label="Membership levels" options={options.memberships} value={f.membership_keys} onChange={set("membership_keys")} testid="agreement-pick-memberships" />}
              {targets.includes("service_types") && <MultiPick label="Session types" options={options.service_types} value={f.service_types} onChange={set("service_types")} testid="agreement-pick-types" />}
              {targets.includes("locations") && <MultiPick label="Centers" options={options.locations} value={f.location_ids} onChange={set("location_ids")} testid="agreement-pick-locations" />}
            </div>
            {targets.includes("scoped_hint") && <p className="text-[11px] text-[#8FA89B] mt-2">Leave everything unselected to require it for all of your bookings.</p>}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm mt-4">
          <input type="checkbox" checked={f.active} onChange={(e) => set("active")(e.target.checked)} className="accent-[#144F75]" data-testid="agreement-editor-active" /> Active
        </label>
        <div className="flex gap-2 mt-5 sticky bottom-0 bg-[#FDFBF7] pt-2">
          <button onClick={onClose} className="eyw-btn-ghost flex-1 justify-center">Cancel</button>
          <button onClick={save} disabled={busy === "save" || !f.title.trim()} data-testid="agreement-editor-save" className="eyw-btn flex-1 justify-center disabled:opacity-50">
            {busy === "save" && <Loader2 size={16} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
